import { CheckCircleOutlined, CloseCircleOutlined, SendOutlined } from "@ant-design/icons";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, message, Spin, Tooltip } from "antd";
import GemsLayer from "../../../layers/GemsLayer/GemsLayer";
import UserDetailsLayer from "../../../layers/UserDetailsLayer/UserDetailsLayer";
import { useGlobalDrawer } from "../../../providers/DriverProvider/DriverProvider";
import { useSupportWebSocket } from "../../../providers/SupportWebSocketProvider/SupportWebSocketProvider";
import supportApi from "../../../api/supportApi";
import usersApi from "../../../api/usersApi";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

function ticketStatusPill(status) {
    if (status === "OPEN") return "border-rose-400/35 bg-rose-500/12 text-rose-100";
    if (status === "CLOSED") return "border-white/15 bg-white/[0.08] text-white/55";
    if (status === "IN_PROGRESS") return "border-sky-400/35 bg-sky-500/12 text-sky-100";
    if (status === "WAITING_USER") return "border-amber-400/35 bg-amber-500/12 text-amber-100";
    if (status === "WAITING_SUPPORT") return "border-violet-400/35 bg-violet-500/12 text-violet-100";
    return "border-white/15 bg-white/[0.08] text-white/75";
}

const ChatWindow = ({ chat: ticket, onTicketUpdate, onNewMessage }) => {
    const { openDrawer } = useGlobalDrawer();
    const { socket, isConnected } = useSupportWebSocket();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [userProfile, setUserProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const messagesEndRef = useRef(null);
    const ticketRef = useRef(ticket);

    useEffect(() => {
        ticketRef.current = ticket;
    }, [ticket]);

    const fetchTicket = useCallback(async () => {
        if (!ticket?.id) {
            setMessages([]);
            return;
        }

        try {
            setLoading(true);
            const ticketData = await supportApi.getTicketById(ticket.id);
            setMessages(ticketData.messages || []);

            if (ticketData.userId) {
                setLoadingProfile(true);
                try {
                    const profile = await usersApi.getProfile(ticketData.userId);
                    setUserProfile(profile);
                } catch (error) {
                    console.error("Ошибка при загрузке профиля:", error);
                } finally {
                    setLoadingProfile(false);
                }
            }

            await supportApi.markAsRead(ticket.id);
        } catch (error) {
            console.error("Ошибка при загрузке тикета:", error);
            message.error("Не удалось загрузить тикет");
        } finally {
            setLoading(false);
        }
    }, [ticket?.id]);

    useEffect(() => {
        fetchTicket();
    }, [fetchTicket]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (!onNewMessage) return;

        const messageHandler = (ticketId, msg) => {
            if (ticket?.id && String(ticket.id) === String(ticketId)) {
                setMessages((prev) => {
                    const exists = prev.find((m) => m.id === msg.id);
                    if (exists) return prev;
                    return [...prev, msg];
                });
            }
        };

        window.chatWindowMessageHandler = messageHandler;

        return () => {
            delete window.chatWindowMessageHandler;
        };
    }, [ticket?.id, onNewMessage]);

    useEffect(() => {
        if (!socket || !isConnected) {
            return;
        }

        const handleNewMessage = (data) => {
            let ticketId;
            let newMessage;

            if (data.ticketId && data.message) {
                ticketId = data.ticketId;
                newMessage = data.message;
            } else if (data.ticket && data.message) {
                ticketId = data.ticket.id || data.ticket;
                newMessage = data.message;
            } else {
                return;
            }

            if (ticket?.id && String(ticket.id) === String(ticketId)) {
                setMessages((prev) => {
                    const exists = prev.find((m) => m.id === newMessage.id);
                    if (exists) return prev;
                    return [...prev, newMessage];
                });
            }
        };

        socket.on("ticket:message", handleNewMessage);

        return () => {
            if (socket) {
                socket.off("ticket:message", handleNewMessage);
            }
        };
    }, [socket, isConnected, ticket?.id]);

    const handleSendMessage = async () => {
        if (!messageText.trim() || !ticket?.id) return;

        const textToSend = messageText.trim();
        setMessageText("");

        try {
            setSending(true);
            const newMessage = await supportApi.sendMessage(ticket.id, textToSend);
            setMessages((prev) => [...prev, newMessage]);
            if (onTicketUpdate) {
                onTicketUpdate();
            }
        } catch (error) {
            console.error("Ошибка при отправке сообщения:", error);
            message.error("Не удалось отправить сообщение");
            setMessageText(textToSend);
        } finally {
            setSending(false);
        }
    };

    const handleAssign = async () => {
        if (!ticket?.id) return;

        try {
            await supportApi.assignTicket(ticket.id);
            message.success("Тикет назначен вам");
            if (onTicketUpdate) {
                onTicketUpdate();
            }
        } catch (error) {
            console.error("Ошибка при назначении тикета:", error);
            message.error("Не удалось назначить тикет");
        }
    };

    const handleClose = async () => {
        if (!ticket?.id) return;

        try {
            await supportApi.closeTicket(ticket.id);
            message.success("Тикет закрыт");
            if (onTicketUpdate) {
                onTicketUpdate();
            }
        } catch (error) {
            console.error("Ошибка при закрытии тикета:", error);
            message.error("Не удалось закрыть тикет");
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat("ru-RU", {
                hour: "2-digit",
                minute: "2-digit",
                day: "2-digit",
                month: "2-digit",
            }).format(date);
        } catch {
            return "";
        }
    };

    const getUserDisplayName = () => {
        if (userProfile?.username) return userProfile.username;
        if (ticket?.telegramUsername) return ticket.telegramUsername;
        return `ID: ${ticket?.userId || "?"}`;
    };

    const userDetail = async () => {
        if (!ticket?.telegramId && !ticket?.userId) {
            message.warning("Нет данных о пользователе");
            return;
        }

        try {
            setLoadingProfile(true);
            let foundUser = null;

            if (ticket.userId) {
                try {
                    const profile = await usersApi.getProfile(ticket.userId);
                    foundUser = profile;
                } catch {
                    /* try telegram */
                }
            }

            if (!foundUser && ticket.telegramId) {
                foundUser = await usersApi.findByTelegramId(ticket.telegramId);
            }

            if (foundUser) {
                try {
                    const fullProfile = await usersApi.getProfile(foundUser.id);
                    openDrawer(<UserDetailsLayer user={fullProfile} />, { width: 440 });
                } catch {
                    openDrawer(<UserDetailsLayer user={foundUser} />, { width: 440 });
                }
            } else {
                openDrawer(
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-white backdrop-blur-sm">
                        <div className="text-center">
                            <div className="mb-4 text-6xl opacity-90">👤</div>
                            <h2 className="mb-2 text-xl font-semibold tracking-tight text-white/95">
                                Пользователь не найден
                            </h2>
                            <p className="mb-4 text-sm text-white/45">
                                Этот пользователь не является пользователем octoCrystaks
                            </p>
                            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-left">
                                <div className="mb-2 text-sm text-white/70">
                                    <span className="font-medium text-white/45">Telegram ID: </span>
                                    {ticket.telegramId || "N/A"}
                                </div>
                                <div className="mb-2 text-sm text-white/70">
                                    <span className="font-medium text-white/45">Username: </span>
                                    {ticket.telegramUsername || "N/A"}
                                </div>
                                {ticket.userId && (
                                    <div className="text-sm text-white/70">
                                        <span className="font-medium text-white/45">User ID: </span>
                                        {ticket.userId}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }
        } catch (error) {
            console.error("Ошибка при поиске пользователя:", error);
            message.error("Не удалось загрузить данные пользователя");
        } finally {
            setLoadingProfile(false);
        }
    };

    if (!ticket) {
        return (
            <div
                className={cn(
                    shellClass,
                    "flex min-h-[280px] min-w-0 flex-1 flex-col items-center justify-center p-10 text-center"
                )}
            >
                <div className="pointer-events-none absolute inset-0 opacity-40">
                    <div className="absolute right-0 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-fuchsia-500/15 blur-3xl" />
                </div>
                <div className="relative max-w-sm">
                    <p className="mb-2 text-[16px] font-semibold text-white/90">Выберите тикет</p>
                    <p className="text-[13px] leading-relaxed text-white/45">
                        Откройте диалог из списка слева, чтобы отвечать пользователям в том же стиле, что и остальная
                        админка.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn(shellClass, "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden")}>
            <div className="pointer-events-none absolute -right-16 top-0 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />

            <header className="relative flex shrink-0 flex-col gap-3 border-b border-white/10 bg-white/[0.03] p-3 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:p-4">
                <button
                    type="button"
                    onClick={userDetail}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-transparent p-1 text-left transition hover:border-white/10 hover:bg-white/[0.04]"
                >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.08] text-base font-semibold text-white/90">
                        {ticket.telegramUsername?.[0]?.toUpperCase() || "·"}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                            <h3 className="truncate text-[15px] font-semibold text-white/95">{getUserDisplayName()}</h3>
                            {loadingProfile && (
                                <Spin size="small" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span
                                className={cn(
                                    "inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
                                    ticketStatusPill(ticket.status)
                                )}
                            >
                                {ticket.status}
                            </span>
                            {ticket.telegramId && (
                                <span className="text-[11px] text-white/40">@{ticket.telegramId}</span>
                            )}
                        </div>
                    </div>
                </button>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {!ticket.assignedToUserId && ticket.status !== "CLOSED" && (
                        <Tooltip title="Назначить тикет">
                            <Button
                                type="default"
                                icon={<CheckCircleOutlined />}
                                onClick={handleAssign}
                                className="!rounded-xl !border-emerald-400/30 !bg-emerald-500/10 !text-emerald-100 hover:!border-emerald-400/50 hover:!bg-emerald-500/20"
                            >
                                Назначить
                            </Button>
                        </Tooltip>
                    )}
                    {ticket.status !== "CLOSED" && (
                        <Tooltip title="Закрыть тикет">
                            <Button
                                type="default"
                                icon={<CloseCircleOutlined />}
                                onClick={handleClose}
                                className="!rounded-xl !border-rose-400/35 !bg-rose-500/10 !text-rose-100 hover:!border-rose-400/55 hover:!bg-rose-500/20"
                            >
                                Закрыть
                            </Button>
                        </Tooltip>
                    )}
                    <div className="max-w-full overflow-x-auto rounded-xl border border-white/10 md:max-w-[340px]">
                        <GemsLayer bg="bg-white/[0.04]" />
                    </div>
                </div>
            </header>

            <div className="relative min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                {loading ? (
                    <div className="flex h-full min-h-[200px] items-center justify-center">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex h-full min-h-[200px] items-center justify-center text-[13px] text-white/45">
                        Нет сообщений
                    </div>
                ) : (
                    <>
                        {messages.map((msg) => {
                            const isSupport = msg.messageType === "SUPPORT";
                            const isSystem = msg.messageType === "SYSTEM";

                            return (
                                <div
                                    key={msg.id}
                                    className={cn("flex", isSystem ? "justify-center" : isSupport ? "justify-end" : "justify-start")}
                                >
                                    <div
                                        className={cn(
                                            "max-w-[min(100%,520px)] rounded-2xl px-4 py-2.5",
                                            isSystem &&
                                                "border border-white/10 bg-white/[0.05] text-center text-[13px] text-white/50",
                                            isSupport &&
                                                "border border-emerald-400/25 bg-gradient-to-br from-emerald-500/25 to-emerald-600/15 text-white shadow-[0_8px_32px_-16px_rgba(16,185,129,0.4)]",
                                            !isSystem &&
                                                !isSupport &&
                                                "border border-white/10 bg-white/[0.08] text-white/90"
                                        )}
                                    >
                                        <div className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
                                            {msg.content}
                                        </div>
                                        <div
                                            className={cn(
                                                "mt-1 text-[11px]",
                                                isSupport ? "text-emerald-100/70" : "text-white/35"
                                            )}
                                        >
                                            {formatTime(msg.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {ticket.status !== "CLOSED" && (
                <footer className="relative flex shrink-0 flex-col gap-2 border-t border-white/10 bg-white/[0.03] p-3 backdrop-blur-sm sm:flex-row sm:items-end sm:gap-3 sm:p-4">
                    <textarea
                        rows={2}
                        placeholder="Сообщение… (Enter — отправить, Shift+Enter — новая строка)"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        disabled={sending}
                        className="min-h-[48px] flex-1 resize-none rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-[13px] text-white/90 placeholder:text-white/30 focus:border-emerald-400/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        loading={sending}
                        disabled={!messageText.trim()}
                        className="!h-11 !rounded-xl !border-0 !bg-emerald-500 !px-5 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        Отправить
                    </Button>
                </footer>
            )}
        </div>
    );
};

export default ChatWindow;
